import { useCallback, useState } from 'react';
import {
  fetchLoadExecutionExport,
  AppliedFiltersForLoadExecution,
} from '../services/onTimeDispatchLoadExecutionApi';
import { useToast } from '../../../../../components/LhToastContainer';
import { Platform } from 'react-native';

export interface UseLoadExecutionExportReturn {
  isExporting: boolean;
  error: string | null;
  exportLoadExecutionData: (
    appliedFilters: AppliedFiltersForLoadExecution,
    adminLocations: any[],
    sortBy?: string,
    sortDirection?: 'ASC' | 'DESC'
  ) => Promise<void>;
}

/**
 * Hook to handle Load Execution data export
 * Manages export state, API calls, and file download
 * Prevents concurrent exports with loading state gate
 * 
 * Features:
 * - Paginates the same /ontime-dispatch/load-execution endpoint used by the table
 * - Honors the table's current sort so the CSV matches on-screen order
 * - Triggers browser download with timestamped filename
 * - Shows toast notifications on success/error
 * - Prevents duplicate concurrent exports
 * 
 * @returns Export state and trigger function
 */
export function useLoadExecutionExport(): UseLoadExecutionExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const exportLoadExecutionData = useCallback(
    async (
      appliedFilters: AppliedFiltersForLoadExecution,
      adminLocations: any[],
      sortBy?: string,
      sortDirection?: 'ASC' | 'DESC'
    ) => {
      // Prevent concurrent exports
      if (isExporting) {
        console.warn('[Load Execution Export Hook] Export already in progress');
        return;
      }

      try {
        setIsExporting(true);
        setError(null);

        // Paginates /ontime-dispatch/load-execution and builds the CSV client-side
        const exportResult = await fetchLoadExecutionExport(
          appliedFilters,
          adminLocations,
          sortBy,
          sortDirection
        );
        const {
          csvBlob,
          ssccFetchAttemptCount,
          ssccFetchFailureCount,
        } = exportResult;

        // Trigger download (Web platform)
        if (Platform.OS === 'web' && typeof document !== 'undefined') {
          const url = URL.createObjectURL(csvBlob);
          const anchor = document.createElement('a');
          anchor.href = url;

          // Filename: OnTimeDispatch_LoadExecution_[YYYY-MM-DD].csv
          const dateStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
          const exportFileName = `OnTimeDispatch_Loads_${dateStr}.csv`;
          anchor.download = exportFileName;

          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);

          // Clean up object URL to free memory
          URL.revokeObjectURL(url);

          console.log('[Load Execution Export Hook] File downloaded successfully:', exportFileName);

          // Show "Export started" toast with generated file name per spec
          showToast({
            header: 'Export started',
            subtext: exportFileName,
            variant: 'success',
            duration: 3000,
          });

          if (ssccFetchFailureCount > 0) {
            showToast({
              header: 'Export completed with partial SSCC details',
              subtext: `${ssccFetchFailureCount} of ${ssccFetchAttemptCount} SSCC breakdown requests failed. Some SSCC columns may be blank.`,
              variant: 'warning',
              duration: 5000,
            });
          }
        } else {
          throw new Error(
            'Export is only supported on web platform'
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to export Load Execution data';
        
        setError(errorMessage);
        console.error('[Load Execution Export Hook] Export failed:', errorMessage);

        // Show error toast
        showToast({
          header: 'Export Failed',
          subtext: errorMessage,
          variant: 'error',
          duration: 5000,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, showToast]
  );

  return {
    isExporting,
    error,
    exportLoadExecutionData,
  };
}
